import { useSelector } from "react-redux";
import {
    FileData,
    useAddMediaSourceMutation,
    useGetTasksQuery,
    useLinkFilesToMediaSourceMutation,
    useRequestAnalysisMutation,
} from "../../../../../../../store/api/kinesense";
import { AnalysisRequestState, SummaryPageProps } from "../SummaryPage";
import { ApplicationState } from "../../../../../../../store";
import { MediaSource } from "../../../../../../../models/media/MediaSource";
import { Notifications } from "../../../../../../../utilities/Notifications/Notifications";
import { tryExtractErrorMessage } from "../../../../../../../utilities/helpers";

export type RequestAnalysisHandlerProps = SummaryPageProps & {
    setUploadState: (_: React.SetStateAction<AnalysisRequestState>) => void;
    setUploadedMediaSource: (_: React.SetStateAction<MediaSource>) => void;
    setupFiles: (_: MediaSource) => Promise<FileData[]>;
    resetFiles: () => void;
};

/** Handle logic for requesting analysis, including creating and linking media sources */
export default function useHandleRequestAnalysis(props: RequestAnalysisHandlerProps) {
    const { general } = useSelector((state: ApplicationState) => state);
    const projectId = general?.activeProjectId;

    const [requestAnalysis] = useRequestAnalysisMutation();
    const [linkFilesToMediaSource] = useLinkFilesToMediaSourceMutation();
    const [addMediaSource] = useAddMediaSourceMutation();

    const { data: tasks, refetch: refetchTasks } = useGetTasksQuery({ projectId }, { skip: !projectId });

    async function createNewMediaSource(): Promise<MediaSource> {
        return addMediaSource({
            name: props.request.name.data?.name,
            startsAt: props.request.name.data?.startTime?.valueOf(),
            // Calculated by back end when linking files
            duration: 0,
            type: "video",
            metadata: props.request.metadata.data,
            projectId,
        })
            .unwrap()
            .then((mediaSource) => {
                return mediaSource;
            })
            .catch((e) => {
                Notifications.notify(
                    "Failed to add media source",
                    `The following error was encountered while adding the media source: "${tryExtractErrorMessage(e)}"`,
                    "warning",
                    "errorAnalysisRequest",
                );
                throw e;
            });
    }

    function handleError(error) {
        console.error(error);

        props.resetFiles();
        props.setUploadState("failed");
        props.setHideNavButtons(false);
    }

    function getAnalysisProfile(): string | undefined {
        const profile = props.request.algorithm.data?.standardOptions.analysisProfile;

        if (profile && typeof profile !== "string") {
            return undefined;
        }

        return profile as string;
    }

    async function sendAnalysisRequest(mediaSource: MediaSource) {
        const profile = getAnalysisProfile();

        const { standardOptions, analyserOptions } = props.request.algorithm.data;
        const analysisRequest = {
            analyserId: props.request.algorithm.data?.analyserId,
            configuration: { standardOptions, analyserOptions } as AnalyserOptions,
            profile: profile,
            frameRateOverride: undefined,
            analysisBounds: undefined,
        };

        return requestAnalysis({
            mediaId: mediaSource.mediaId,
            analysisRequest: analysisRequest,
        })
            .unwrap()
            .then(async (response) => {
                console.log(response);

                // HACK: re-fetch until newly created task appears in the task list
                let nextTasks = tasks;
                let retries = 0;
                while (
                    retries <= 5 &&
                    nextTasks.find((t) => t.references.mediaId === mediaSource.mediaId) === undefined
                ) {
                    await new Promise((r) => setTimeout(r, 1000));
                    nextTasks = await refetchTasks().unwrap();
                    retries += 1;
                }

                props.setUploadState("requested");
            })
            .catch((e) => {
                Notifications.notify(
                    "Error with requesting analysis",
                    `The following error was encountered while requesting analysis: "${tryExtractErrorMessage(e)}"`,
                    "warning",
                    "errorAnalysisRequest",
                );
                throw e;
            });
    }

    async function handleRequestAnalysis() {
        props.setHideNavButtons(true);

        try {
            console.log("Creating new media source");
            const mediaSource = await createNewMediaSource();

            const files = await props.setupFiles(mediaSource);
            const fileIds = files.map((f) => f.fileId);

            props.setUploadedMediaSource(mediaSource);

            // link file to media source
            console.log(`Linking file(s) '${fileIds.join(", ")}' to media source '${mediaSource.mediaId}'`);
            await linkFilesToMediaSource({
                projectId: general.activeProjectId,
                mediaId: mediaSource.mediaId,
                files,
            })
                .unwrap()
                .catch((e) => {
                    Notifications.notify(
                        "Error with the uploaded file",
                        `The following error was encountered with the uploaded file: "${tryExtractErrorMessage(e)}"`,
                        "warning",
                    );
                    throw e;
                });

            console.log("Requesting analysis");
            await sendAnalysisRequest(mediaSource);
        } catch (e) {
            handleError(e);
        }
    }

    return {
        handleRequestAnalysis,
    };
}
